/**
 * variant-availability.js
 *
 * Softens the theme's cross-option "unavailable" strike-through so an option
 * value that is available in *some* combination is not struck through just
 * because it doesn't exist for the currently-selected later options.
 *
 * The vendor runtime (xo-webcomponents.js) marks each option <input> with
 * `xo-disabled` / `xo-unavailable` by pinning EVERY other selected option
 * (`{ ...variantSelected, [name]: value }`). That strikes e.g. "lemon" when
 * "size=20x40g" is selected, even though lemon exists in another size.
 *
 * We apply a left-to-right rule instead: an option value is considered
 * available if some AVAILABLE variant contains it together with the selected
 * values of the option groups BEFORE it (later groups are free). We mark such
 * inputs with `data-xo-soft-available`; a higher-specificity rule in custom.css
 * then cancels the vendor strike on those pills.
 *
 * We only ever ADD our own marker (never touch the vendor's attributes), and a
 * relaxed rule can only un-strike a subset of what the vendor strikes, so there
 * is no feedback loop with the vendor's `bindAvailable`.
 *
 * Note: this is a purely visual change. The vendor's `reselectAvailableVariants`
 * already auto-snaps the other options to a valid combination when an
 * incompatible value is clicked.
 */
(function () {
  'use strict';

  function initProduct(productEl) {
    var dataEl = productEl.querySelector('.xo-variant-availability-data');
    if (!dataEl) return;

    var variants;
    try {
      variants = JSON.parse(dataEl.textContent);
    } catch (e) {
      return;
    }
    if (!Array.isArray(variants) || !variants.length) return;

    // Option groups in DOM order (= product.options order). Exclude quick-view
    // clones so we only touch the main product's own selector.
    function groups() {
      return Array.prototype.filter.call(
        productEl.querySelectorAll('xo-product-variant'),
        function (g) {
          return !g.closest('[xo-for-quick-view]');
        }
      );
    }

    function selectedValue(groupEl) {
      var checked = groupEl.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    }

    // Available if some in-stock variant contains `value` AND all of the
    // already-selected values of the prior option groups.
    function isSoftAvailable(priorSelected, value) {
      return variants.some(function (variant) {
        if (!variant.a) return false;
        var opts = variant.o;
        if (opts.indexOf(value) === -1) return false;
        for (var i = 0; i < priorSelected.length; i++) {
          if (priorSelected[i] != null && opts.indexOf(priorSelected[i]) === -1) {
            return false;
          }
        }
        return true;
      });
    }

    function mark(input, ok) {
      var has = input.hasAttribute('data-xo-soft-available');
      if (ok && !has) {
        input.setAttribute('data-xo-soft-available', '');
      } else if (!ok && has) {
        input.removeAttribute('data-xo-soft-available');
      }
    }

    function update() {
      var gs = groups();
      var priorSelected = [];
      for (var p = 0; p < gs.length; p++) {
        var inputs = gs[p].querySelectorAll('input[type="radio"]');
        for (var j = 0; j < inputs.length; j++) {
          mark(inputs[j], isSoftAvailable(priorSelected, inputs[j].value));
        }
        priorSelected.push(selectedValue(gs[p]));
      }
    }

    var scheduled = false;
    function schedule() {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(function () {
        scheduled = false;
        update();
      });
    }

    productEl.addEventListener('change', function (event) {
      if (event.target && event.target.closest && event.target.closest('xo-product-variant')) {
        schedule();
      }
    });

    update();
    // Re-run after the vendor upgrades inputs / runs its initial auto-snap.
    requestAnimationFrame(update);
  }

  function init() {
    var products = document.querySelectorAll('xo-product[xo-product-information]');
    Array.prototype.forEach.call(products, initProduct);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
